/* eslint-disable react/no-array-index-key */
import { ClbCheckbox } from '@celebration/react/ClbCheckbox/ClbCheckbox';
import React, { useCallback, useEffect, useState } from 'react';
import { ClbLoading } from '@celebration/react/ClbLoading/ClbLoading';
import { useTranslation } from 'react-i18next';
import Shape from '../../../../../../common/components/shape';
import { ILocation } from '../../../../../../common/interfaces/ILocation';
import IBranchFormRegionsProps from './interfaces/IBranchFormRegionsProps';
import { IRegionLocations } from '../../../../../../common/interfaces/IRegionLocations';
import { ICheckboxProps } from './interfaces/ICheckboxProps';
import Resume from '../../../../../../common/components/branches/resume/index';
import BranchFormService from '../../services/branch-form.service';
import BranchService from '../../../../../../common/services/branch';
import IBranchRegion from '../../../../../../common/interfaces/IBranchRegion';
import { useToast } from '../../../../../../app/contexts/toast/toast.context';

function BranchFormRegions({
    branchFormData,
    regionData,
    carrierId,
    carrierContactData,
}: Readonly<IBranchFormRegionsProps>) {
    const { t } = useTranslation();

    const REGIONS_MAIN_TITLE = t('RegioesAtendidas');

    const { setToast } = useToast();

    const [showLoading, setShowLoading] = useState<boolean>(true);
    const [regions, setRegions] = useState([] as IRegionLocations[]);

    const getSelectedLocations = useCallback(async () => {
        const selectedRegions = branchFormData.id
            ? await BranchService.getBranchLocations(
                  carrierId,
                  branchFormData.id,
                  t,
              )
            : ([] as IBranchRegion[]);

        return selectedRegions.flatMap(r => r.locations);
    }, [branchFormData.id, carrierId, t]);

    const fetchData = useCallback(async () => {
        try {
            const regions = await BranchFormService.getRegionsList();

            const selectedLocations = await getSelectedLocations();

            setRegions(regions);

            regionData.current = regions;

            regions.forEach(region => {
                region.locations.forEach(location => {
                    location.isChecked = selectedLocations.some(
                        l => l.id === location.id,
                    );
                    location.parent = region.name;
                });

                region.isChecked = region.locations.every(
                    location => location.isChecked,
                );
            });
        } catch (error: any) {
            setToast('negative', t('Erro'), (error as Error).message);
        } finally {
            setShowLoading(false);
        }
    }, [regionData, getSelectedLocations, setToast, t]);

    useEffect(() => {
        if (regionData.current.length === 0 && showLoading) {
            fetchData();
        } else {
            setShowLoading(false);
            setRegions(regionData.current);
        }
    }, [fetchData, regionData, regions, showLoading]);

    const selectAllCheck = useCallback(
        (props: ICheckboxProps) => {
            const regionsNotUpdated = [...regions];

            regionsNotUpdated.forEach(region => {
                if (region.abbreviation === props.id) {
                    region.isChecked = props.checked;
                    region.locations.forEach(element => {
                        element.isChecked = props.checked;
                    });
                }
            });
            setRegions(regionsNotUpdated);
            regionData.current = regionsNotUpdated;
        },
        [regions, regionData],
    );

    const selectedCheck = useCallback(
        (props: ICheckboxProps) => {
            const regionsNotUpdated = [...regions];

            regionsNotUpdated.forEach(region => {
                region.locations.forEach(element => {
                    if (element.id === props.id) {
                        element.isChecked = props.checked;
                    }
                });

                region.isChecked = region.locations.every(
                    location => location.isChecked,
                );
            });

            setRegions(regionsNotUpdated);
            regionData.current = regionsNotUpdated;
        },
        [regions, regionData],
    );

    const renderLocations = (region: IRegionLocations) => {
        const data = region.locations;

        return (
            <div className="locations-list">
                {data.map((currentElement: ILocation, index: number) => {
                    return (
                        <div
                            className="location-item"
                            key={`location-${index}`}
                        >
                            <div className="location-item-checkbox">
                                <ClbCheckbox
                                    name="Checkbox"
                                    checked={currentElement.isChecked}
                                    id={currentElement.id}
                                    data-testid={`checkbox-${currentElement.parent}-${currentElement.name}`}
                                    onChange={(e: any) =>
                                        selectedCheck({
                                            id: e.target.id,
                                            checked: e.target.checked,
                                        })
                                    }
                                >
                                    {`${currentElement.code} - ${currentElement.name}`}
                                </ClbCheckbox>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderRegions = () => {
        return (
            <div>
                {regions.map(
                    (currentElement: IRegionLocations, index: number) => {
                        return (
                            <div key={`region-${index}`}>
                                <div className="regions-top">
                                    <h2>{t(currentElement.name)}</h2>
                                    <ClbCheckbox
                                        name="Checkbox"
                                        checked={currentElement?.isChecked}
                                        id={currentElement.abbreviation}
                                        data-testid={`checkbox-${currentElement.name}`}
                                        onChange={(e: any) =>
                                            selectAllCheck({
                                                id: e.target.id,
                                                checked: e.target.checked,
                                            })
                                        }
                                    >
                                        {t('SelecionarTodas')}
                                    </ClbCheckbox>
                                </div>
                                {renderLocations(currentElement)}
                            </div>
                        );
                    },
                )}
            </div>
        );
    };

    const renderErrorMessage = () => {
        return (
            regions.length === 0 &&
            showLoading === false && (
                <h2 className="regions-error-message">
                    Nenhuma Região para ser exibida
                </h2>
            )
        );
    };

    const renderRegionsShape = () => {
        return (
            <Shape className="regions-shape">
                <h1> {REGIONS_MAIN_TITLE} </h1>
                {showLoading && <ClbLoading />}
                {renderRegions()}
                {renderErrorMessage()}
            </Shape>
        );
    };

    const renderResumeComponent = () => {
        return (
            <Resume
                branchData={{
                    cnpj: branchFormData.cnpj,
                    corporateName: branchFormData.corporateName,
                    tmsCode: branchFormData.tmsCode,
                    sapCode: branchFormData.sapCode,
                    eccCode: branchFormData.eccCode,
                    mainContact: branchFormData.mainContact,
                    additionalContacts: branchFormData.additionalContacts,
                    paymentTerm: branchFormData.paymentTerm,
                }}
                carrierContactData={carrierContactData}
                key="resume-regions"
            />
        );
    };

    return (
        <div className="container">
            {renderRegionsShape()}

            <Shape className="resume-shape">{renderResumeComponent()}</Shape>
        </div>
    );
}

export default BranchFormRegions;
